import React, { FC } from 'react';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { Table, TableProps } from '@components/atoms/Table';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { useMediaQuery } from '@hooks/useMediaQuery';

interface OrderDetailManagementTableProps {
    header: TableProps['headerCols'];
    content: {
        cells: { classes?: string; content: string | JSX.Element; acceptsHTML?: boolean }[];
        options: { icon: string; action: () => void }[];
    }[];
    hideActionsOnDesktop?: boolean;
    withActions?: boolean;
}

export const OrderDetailManagementTable: FC<OrderDetailManagementTableProps> = ({
    header,
    content,
    hideActionsOnDesktop,
    withActions,
}) => {
    const pageSize = useMediaQuery();
    const { t } = useTranslation();

    const translations = {
        actions: t('order-detail-management-table-component.actions'),
    };

    const contentCommonClasses = ['xs', 'sm', 'md'].includes(pageSize) ? 'text-truncate' : '';

    return (
        <Table
            compressedSize={true}
            headerCols={
                withActions === false
                    ? header
                    : header
                      ? [...header, { classes: 'text-end', hideOnDesktop: hideActionsOnDesktop, text: translations.actions }]
                      : []
            }
            contentRows={content.map((row) => {
                const cells = row.cells.map((cell) => {
                    return {
                        classes: cell.classes,
                        content: (
                            <div className="d-grid">
                                <Typography classes={contentCommonClasses} fontSize="16" tagVariant={TypographyTagVariants.span}>
                                    {cell.acceptsHTML && typeof cell.content === 'string' ? (
                                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cell.content) }}></div>
                                    ) : (
                                        cell.content
                                    )}
                                </Typography>
                            </div>
                        ),
                    };
                });

                return withActions === false
                    ? cells
                    : [
                          ...cells,
                          {
                              classes: 'text-end',
                              content: (
                                  <Table.Options
                                      classes="gap-3"
                                      options={row.options.map((item, index) => (
                                          <button key={`option-${index}`} onClick={item.action}>
                                              <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                  <i className={item.icon} />
                                              </Typography>
                                          </button>
                                      ))}
                                  />
                              ),
                          },
                      ];
            })}
        />
    );
};
